import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { ChevronLeft, ChevronRight, ExternalLink, ImageOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { FeedsFrame, Pill } from '../components/FeedsNav'
import { useDestinations, useFeedItems, useMerchThemes } from '../hooks/useFeeds'
import { REASON_LABEL, REASONS, type FeedItem, type ItemKind, type ItemParams } from '../api/feedsApi'

const ALL = '__all__'
const PAGE = 50

function Thumb({ src }: { src: string }) {
  const [broken, setBroken] = useState(false)
  if (!src || broken) {
    return (
      <div className="flex h-12 w-12 items-center justify-center rounded bg-slate-100 text-slate-400">
        <ImageOff className="h-4 w-4" />
      </div>
    )
  }
  return <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} className="h-12 w-12 rounded object-cover" />
}

function Labels({ item }: { item: FeedItem }) {
  const a = item.attrs
  const labels = [
    ['0', a.custom_label_0], ['1 room', a.custom_label_1], ['2', a.custom_label_2], ['3 tier', a.custom_label_3], ['4 lifecycle', a.custom_label_4],
  ].filter(([, v]) => v)
  return (
    <div className="flex flex-wrap gap-1">
      {labels.map(([k, v]) => (
        <span key={k} title={`custom_label_${k.split(' ')[0]}`} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">
          {v}
        </span>
      ))}
    </div>
  )
}

export function ItemsPage() {
  const [params, setParams] = useSearchParams()
  const destination = params.get('destination') || 'google-us'
  const kind = (params.get('kind') as ItemKind | null) || undefined
  const eligibleParam = params.get('eligible')
  const eligible = eligibleParam === 'true' ? true : eligibleParam === 'false' ? false : undefined
  const reason = params.get('reason') || undefined
  const theme = params.get('theme') || undefined
  const q = params.get('q') || undefined
  const [search, setSearch] = useState(q ?? '')
  // Keyset pages: the cursors that opened each page so far (page 1 opens with none).
  const [cursors, setCursors] = useState<string[]>([])

  const filterKey = [destination, kind, eligibleParam, reason, theme, q].join('|')
  useEffect(() => setCursors([]), [filterKey])
  useEffect(() => setSearch(q ?? ''), [q])

  const query: ItemParams = { destination, kind, eligible, reason, theme, q, limit: PAGE, cursor: cursors[cursors.length - 1] }
  const { data, isLoading, isFetching, error } = useFeedItems(query)
  const { data: themes } = useMerchThemes()
  const { data: destinations } = useDestinations()

  const set = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params)
    if (value === undefined || value === '' || value === ALL) next.delete(key)
    else next.set(key, value)
    if (key === 'eligible' && value === 'true') next.delete('reason')
    if (key === 'reason' && value && value !== ALL) next.set('eligible', 'false')
    setParams(next, { replace: true })
  }

  return (
    <FeedsFrame
      title="Feed items"
      description="Every row the last build rendered, eligible or not. Excluded items are never served; the reason is the first rule they failed."
    >
      <div className="flex flex-wrap items-center gap-2">
        {(destinations?.data.length ?? 0) > 1 && (
          <Select value={destination} onValueChange={(v) => set('destination', v)}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              {destinations?.data.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.slug}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={kind ?? ALL} onValueChange={(v) => set('kind', v)}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All kinds</SelectItem>
            <SelectItem value="individual">Individual</SelectItem>
            <SelectItem value="set">Sets</SelectItem>
          </SelectContent>
        </Select>
        <Select value={eligibleParam ?? ALL} onValueChange={(v) => set('eligible', v)}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any eligibility</SelectItem>
            <SelectItem value="true">Eligible</SelectItem>
            <SelectItem value="false">Excluded</SelectItem>
          </SelectContent>
        </Select>
        <Select value={reason ?? ALL} onValueChange={(v) => set('reason', v)}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any reason</SelectItem>
            {REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={theme ?? ALL} onValueChange={(v) => set('theme', v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Any room" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any room</SelectItem>
            {themes?.data.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            set('q', search.trim())
          }}
        >
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, artist, id…" className="w-64" />
          <Button type="submit" size="sm" variant="outline">Search</Button>
        </form>
        {params.toString() && (
          <Button size="sm" variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear</Button>
        )}
      </div>

      {reason && <p className="text-sm text-slate-500"><span className="font-mono">{reason}</span>: {REASON_LABEL[reason]}</p>}
      {error && <p className="text-sm text-red-700">{(error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Could not load items.'}</p>}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16" />
                <TableHead>Title</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Labels</TableHead>
                <TableHead>Changed</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={7}><Skeleton className="h-10 w-full" /></TableCell></TableRow>
              )}
              {!isLoading && data?.data.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">No items match.</TableCell></TableRow>
              )}
              {data?.data.map((item) => (
                <TableRow key={item.itemId} className={isFetching ? 'opacity-60' : undefined}>
                  <TableCell><Thumb src={item.attrs.image_link} /></TableCell>
                  <TableCell className="max-w-md">
                    <div className="truncate font-medium" title={item.attrs.title}>{item.attrs.title || '(untitled)'}</div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="font-mono">{item.itemId}</span>
                      {item.attrs.brand && <span>{item.attrs.brand}</span>}
                      {item.artworkId != null && (
                        <Link to={`/app/artworks/${item.artworkId}`} className="text-sky-700 hover:underline">artwork {item.artworkId}</Link>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">{item.attrs.price || '—'}</TableCell>
                  <TableCell>
                    {item.eligible
                      ? <Pill tone="green">eligible</Pill>
                      : <Pill tone="amber" title={item.exclusionReason ? REASON_LABEL[item.exclusionReason] : undefined}>{item.exclusionReason ?? 'excluded'}</Pill>}
                  </TableCell>
                  <TableCell><Labels item={item} /></TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500" title={item.changedAt}>
                    {item.changedAt ? formatDistanceToNow(new Date(item.changedAt), { addSuffix: true }) : '—'}
                  </TableCell>
                  <TableCell>
                    {item.attrs.link && (
                      <a href={item.attrs.link} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-900" title={item.attrs.link}>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>Page {cursors.length + 1}{data ? ` · ${data.data.length} items` : ''}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={cursors.length === 0} onClick={() => setCursors(cursors.slice(0, -1))}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!data?.nextCursor || isFetching}
            onClick={() => data?.nextCursor && setCursors([...cursors, data.nextCursor])}
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </FeedsFrame>
  )
}
